import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  Spinner,
  Alert,
  Chip,
  EmptyState,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  APP_NAMES,
  ACCESS_LEVELS,
  type StaffMember,
  type AppName,
  type AccessLevel,
  type AppLevels,
} from '../lib/adminApiClient';

/**
 * Staff & roles (capability `staff:manage` = Admin-app Admin level).
 *
 * Reproduces the Figma (admin file UKm146d43Cq16FazC3mAbI, frames 405:2 / 405:346
 * / 405:433). RBAC is PER-APP: each staffer holds an independent level
 * (none/view/manage/admin) on each of the four apps — Consumer / Vendor / Driver
 * / Admin. The ACCESS column shows a C·V·D·A badge per app, coloured by that
 * app's chrome colour and filled by level. Granting Admin on the Admin app (the
 * staff/user/jobs tier) requires typing the person's email to confirm.
 *
 * Server is the source of truth — every action re-checks `staff:manage` and is
 * audited (entityType 'staff').
 */

const NONE_LEVELS: AppLevels = { consumer: 'none', vendor: 'none', driver: 'none', admin: 'none' };

/** App chrome colours (Portal Chrome Color Samples): used to tint the badges. */
const APP_META: Record<AppName, { letter: string; label: string; color: string }> = {
  consumer: { letter: 'C', label: 'Consumer', color: '#25862D' },
  vendor: { letter: 'V', label: 'Vendor', color: '#FF8A00' },
  driver: { letter: 'D', label: 'Driver', color: '#343F5B' },
  admin: { letter: 'A', label: 'Admin', color: '#000000' },
};

const LEVEL_OPTIONS = ACCESS_LEVELS.map((l) => ({ value: l, label: l[0].toUpperCase() + l.slice(1) }));

const fmtDate = (iso: string): string => new Date(iso).toLocaleDateString();

const fullEmail = (email: string) => email.trim().toLowerCase();

// ─── Per-app access badge (C·V·D·A, app-coloured, level-filled) ───────────────
function AccessBadge({ app, level }: { app: AppName; level: AccessLevel }) {
  const m = APP_META[app];
  let bg = '#F2F4F7';
  let fg = '#C0C6CF';
  let border: string | undefined;
  if (level === 'view') {
    bg = `${m.color}24`; // ~14% tint
    fg = m.color;
  } else if (level === 'manage') {
    bg = m.color;
    fg = '#ffffff';
  } else if (level === 'admin') {
    bg = m.color;
    fg = '#ffffff';
    border = '2px solid #B54708'; // amber ring = top level
  }
  return (
    <Box
      title={`${m.label}: ${level}`}
      sx={{
        width: 22,
        height: 22,
        borderRadius: '6px',
        backgroundColor: bg,
        color: fg,
        border,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {m.letter}
    </Box>
  );
}

function AccessBadges({ levels }: { levels: AppLevels }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {APP_NAMES.map((a) => (
        <AccessBadge key={a} app={a} level={levels[a]} />
      ))}
    </Box>
  );
}

type DialogState =
  | { mode: 'assign' }
  | { mode: 'edit'; target: StaffMember }
  | null;

export default function StaffManagement() {
  const caps = useCapabilities();
  const canManage = caps.has('staff:manage');

  const [items, setItems] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listStaff({ q: search || undefined, status: 'all' });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (canManage) void load();
  }, [canManage, load]);

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canManage) {
    return <Alert severity="error">You don’t have permission to manage staff.</Alert>;
  }

  const onSaved = (msg: string) => {
    setFlash({ kind: 'success', msg });
    setDialog(null);
    void load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          Staff &amp; roles
        </Typography>
        <Button
          variant="contained"
          color="primary"
          ariaLabel="Add a staff member"
          buttonText="+ Add staff"
          sx={{ backgroundColor: '#f06500', '&:hover': { backgroundColor: '#d35400' } }}
          onClick={() => setDialog({ mode: 'assign' })}
        />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Manage who can access each GL Plus app — Consumer, Vendor, Driver, Admin — and what they can
        do there. Every change is audited.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }} />
        <TextField
          label="Search staff by name or email"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          size="small"
          sx={{ width: 280 }}
        />
        <Button
          variant="outlined"
          sx={NEUTRAL_BTN}
          ariaLabel="Apply search"
          buttonText="Search"
          onClick={() => setSearch(searchDraft.trim())}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="person" title="No staff yet" body="Add a staff member to grant per-app access." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((s) => (
            <StaffRow key={s.id} staff={s} onEdit={() => setDialog({ mode: 'edit', target: s })} />
          ))}
        </Box>
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
        C&nbsp;Consumer · V&nbsp;Vendor · D&nbsp;Driver · A&nbsp;Admin&nbsp;&nbsp;·&nbsp;&nbsp;colour =
        level (View · Manage · Admin). Only Admin-app Admins can assign or remove staff.
      </Typography>

      {dialog && (
        <AccessDialog
          state={dialog}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
          onError={(msg) => setFlash({ kind: 'error', msg })}
        />
      )}
    </Box>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────
function StaffRow({ staff, onEdit }: { staff: StaffMember; onEdit: () => void }) {
  const disabled = staff.status === 'disabled';
  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {staff.name || staff.email}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {staff.email}
          </Typography>
        </Box>
        <Box sx={{ width: 130 }}>
          <AccessBadges levels={staff.levels} />
        </Box>
        <Box sx={{ width: 90 }}>
          <Chip
            label={disabled ? 'Disabled' : 'Active'}
            color={disabled ? 'default' : 'success'}
            size="small"
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', width: 110 }}>
          added {fmtDate(staff.createdAt)}
        </Typography>
        <Button variant="text" ariaLabel={`Edit ${staff.email}`} buttonText="Edit ▸" onClick={onEdit} />
      </Box>
    </Paper>
  );
}

// ─── Assign / edit dialog ───────────────────────────────────────────────────
function AccessDialog({
  state,
  onClose,
  onSaved,
  onError,
}: {
  state: Exclude<DialogState, null>;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isEdit = state.mode === 'edit';
  const target = isEdit ? state.target : null;

  const [email, setEmail] = useState(target?.email ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [levels, setLevels] = useState<AppLevels>(target ? { ...target.levels } : { ...NONE_LEVELS });
  const [status, setStatus] = useState<'active' | 'disabled'>(target?.status ?? 'active');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const setLevel = (app: AppName, level: AccessLevel) =>
    setLevels((prev) => ({ ...prev, [app]: level }));

  // Grant-Admin guard: setting Admin-app level to admin requires typing the email.
  const elevatingAdmin = levels.admin === 'admin' && (!isEdit || target?.levels.admin !== 'admin');
  const confirmEmail = isEdit ? target!.email : email;
  const confirmOk = !elevatingAdmin || fullEmail(confirm) === fullEmail(confirmEmail);
  const emailOk = isEdit || /.+@.+\..+/.test(email.trim());
  const namesOk = isEdit || (firstName.trim().length > 0 && lastName.trim().length > 0);

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await adminApiClient.updateStaff(target!.id, {
          levels,
          status,
          confirm: elevatingAdmin ? confirm.trim() : undefined,
        });
        onSaved(`Updated access for ${target!.email}.`);
      } else {
        await adminApiClient.assignStaff({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          levels,
          confirm: elevatingAdmin ? confirm.trim() : undefined,
        });
        onSaved(`Invited ${email.trim()} — they’ll get an email with a temporary password.`);
      }
    } catch (err) {
      onError(err instanceof AdminApiError ? err.message : 'Failed to save staff access');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth paperBorderRadius={16}>
      <DialogTitle>
        <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1f2937' }}>
          {isEdit ? target!.name || target!.email : 'Assign staff'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {isEdit
            ? target!.email
            : 'Invite a staff member. They’ll get an email with a temporary password and set up two-step verification at first sign-in.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!isEdit && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 }}>
                Email
              </Typography>
              <TextField
                ariaLabel="Person’s email"
                size="small"
                placeholder="name@glplusinc.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ width: '100%' }}
              />
            </Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 }}>
                  First name
                </Typography>
                <TextField
                  ariaLabel="First name"
                  size="small"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 }}>
                  Last name
                </Typography>
                <TextField
                  ariaLabel="Last name"
                  size="small"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{ width: '100%' }}
                />
              </Box>
            </Box>
          </>
        )}

        <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 1 }}>
          App access
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {APP_NAMES.map((app) => (
            <Box key={app} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AccessBadge app={app} level={levels[app]} />
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '14px' }}>
                {APP_META[app].label}
              </Typography>
              <Select
                ariaLabel={`${APP_META[app].label} access level`}
                value={levels[app]}
                onChange={(_e, v) => setLevel(app, v as AccessLevel)}
                size="small"
                sx={{ width: 150 }}
                options={LEVEL_OPTIONS}
              />
            </Box>
          ))}
        </Box>

        {isEdit && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '14px' }}>Status</Typography>
            <Select
              ariaLabel="Staff status"
              value={status}
              onChange={(_e, v) => setStatus(v as 'active' | 'disabled')}
              size="small"
              sx={{ width: 150 }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'disabled', label: 'Disabled' },
              ]}
            />
          </Box>
        )}

        {elevatingAdmin && (
          <Box sx={{ mt: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2, p: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#7f1d1d', mb: 1 }}>
              ▲ Admin level on the Admin app can manage staff, delete users, and run destructive jobs.
              To confirm, type <strong>{confirmEmail}</strong>.
            </Typography>
            <TextField
              ariaLabel="Type the email to confirm"
              size="small"
              placeholder={confirmEmail}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={{ width: '100%' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button ariaLabel="Cancel" buttonText="Cancel" variant="outlined" sx={NEUTRAL_BTN} size="small" onClick={onClose} />
        <Button
          ariaLabel={isEdit ? 'Save access' : 'Assign'}
          buttonText={busy ? 'Saving…' : isEdit ? 'Save access' : 'Assign'}
          variant="contained"
          size="small"
          disabled={busy || !emailOk || !namesOk || !confirmOk}
          sx={{ backgroundColor: '#f06500', '&:hover': { backgroundColor: '#d35400' } }}
          onClick={() => void save()}
        />
      </DialogActions>
    </Dialog>
  );
}
